/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { EventEmitter } from '@angular/core';
import { hd, UserContextService } from '@nx-fe/services';
import { Activity, isNoteActivity, NoteActivity, UserDetails, UserInfo } from '../../../ticket.model';
import { Observable, of } from 'rxjs';

/** Defines bindings a component should have to function as an activity template */
export interface ActivityTemplateBindings {
  /** The activity data bound to the component */
  activity: Activity;

  /** Indicates whether the note activity is editable or not */
  canEdit: boolean;
}

/** Defines the properties and functions bound by the component */
export interface ActivityBindings {
  /** The activity data bound to the component */
  activity: Activity;

  /** The specified activity type (optional) - used if activity is not provided */
  activityType: string;

  /** The specified activity state (optional) */
  activityState: string;

  /** Invoked when an activity is to be deleted */
  onDelete: any;

  /** Invoked when an activity is to be updated/saved */
  onUpdate: any;

  /** Specifies whether this activity is at the top of a list of activities */
  isHeader: boolean;

  /** Specifies whether this activity is at the bottom of a list of activities */
  isFooter: boolean;

  /** Invoked when the activity list needs to be */
  onRefresh: any;

  /** Specifies whether the user can edit the activity by defined with user's permissions, ticket status */
  canEdit: any | boolean;
}

export class ActivityContextService {
  public transclude: boolean;

  /** See: IActivityBindings.isNotePrivate */
  public isNotePrivate: boolean;

  /** See: IActivityBindings.isResolutionNote */
  public isResolutionNote: boolean;

  /** Stores the resolved creation user display name for the activity */
  public creationUser: string;

  /** Stores the resolved update user display name for the activity */
  public updateUser: string;

  /** default activity template so that unsupported activity types are at least rendered with generic information */
  private defaultComponentName = 'sa-general-activity';

  /** default activity icon class */
  private defaultIconClass = 'hd-icon hd-icon-remote';

  /** default label style css class */
  private defaultIconStateClass = 'label-default';

  /** label style css class when activity is in an error state */
  private errorIconStateClass = '';

  /** Dictionary of supported activity types with their template urls and icon css classes */
  private supportedActivities: {
    [activityTypeId: number]: { activityType: string; component: string; iconClass: string; iconStateClass: string };
  } = {
    // TODO: Add missing mappings?
    // 6	devicelog
    // 7	devicesnapshot
    // 8	devicevideo

    // x: {
    //   activityType: 'description',
    //   component: '',
    //   iconClass: 'hd-icon hd-icon-filetype-document',
    //   iconStateClass: 'icon-description',
    // },
    // x: {
    //   activityType: 'general',

    //   component: 'sa-general-activity',
    //   iconClass: 'hd-icon hd-icon-note',
    //   iconStateClass: 'icon-general',
    // },
    1: {
      activityType: 'note',
      component: 'xs-note-activity',
      iconClass: 'hd-icon hd-icon-note',
      iconStateClass: 'icon-note',
    },
    2: {
      activityType: 'attachment',
      component: 'xs-attachment-activity',
      iconClass: 'hd-icon hd-icon-attachment',
      iconStateClass: 'icon-attachment',
    },
    5: {
      activityType: 'remotecontrol',
      component: 'sa-remote-control-activity',
      iconClass: 'hd-icon hd-icon-cast',
      iconStateClass: 'icon-remotecontrol',
    },
    4: {
      activityType: 'outboundemail',
      component: 'sa-outbound-email-activity',
      iconClass: 'hd-icon hd-icon-email',
      iconStateClass: 'icon-outbound-email',
    },
    3: {
      activityType: 'inboundemail',
      component: 'sa-inbound-email-activity',
      iconClass: 'hd-icon hd-icon-email',
      iconStateClass: 'icon-inbound-email',
    },
    6: {
      activityType: 'devicecapture',
      component: 'xs-device-capture-activity',
      iconClass: 'hd-icon hd-icon-downloadlogs',
      iconStateClass: 'icon-attachment',
    },
    8: {
      activityType: 'devicecapture',
      component: 'xs-device-capture-activity',
      iconClass: 'hd-icon hd-icon-recordVideo',
      iconStateClass: 'icon-attachment',
    },
    9: {
      activityType: 'devicecapture',
      component: 'xs-device-capture-activity',
      iconClass: 'hd-icon hd-icon-screencap',
      iconStateClass: 'icon-attachment',
    },
    //Activity come from the parent element as parameters
    101: {
      activityType: '',
      component: '',
      iconClass: 'hd-icon hd-icon-filetype-document',
      iconStateClass: 'icon-general',
    },
  };

  constructor(
    // private featureToggleService: FeatureToggleService,
    private userContextService: UserContextService,
    public activityTypeId: number,
    public activity: Activity,
    public activityState: string,
    public isHeader: boolean,
    public isFooter: boolean,
    public canEdit: boolean,
    public onDelete: EventEmitter<any> = new EventEmitter(),
    public onUpdate: EventEmitter<any> = new EventEmitter(),
    public onRefresh: EventEmitter<any> = new EventEmitter()
  ) {
    this.errorIconStateClass = 'error';

    if (this.activity !== undefined && isNoteActivity(this.activity)) {
      this.isNotePrivate = this.activity.details.isPrivate;
      this.isResolutionNote = this.activity.details.isResolution;
    }

    // Initialize creation user if it's set
    this.updateCreationUser();
    this.updateUpdateUser();
    this.createIconClass();
    this.createIconStateClass();
  }

  set Activity(activity: Activity) {
    this.activity = activity;
    this.updateCreationUser();
    this.updateUpdateUser();
    this.createIconClass();
  }

  public iconClass: string;
  public iconStateClass: string;
  /** Gets the icon class associated with the activity type - returns a default if not found */
  // eslint-disable-next-line arrow-body-style
  // public getIconClass = (): string => {
  //   return this._iconClass;
  // };

  /** Sets the state of the activity ('active', 'inactive', 'error') */
  public setActivityState = (activityState: string): void => {
    setTimeout(() => {
      this.createIconStateClass();
      this.activityState = activityState;
    });
  };

  /** Gets the state of the activity ('active', 'inactive', 'error') */
  public getActivityState = (): string => this.activityState;

  /** Gets the css class representing the current state of the activity */
  private createIconStateClass = (): void => {
    let iconStateClass = this.defaultIconStateClass;
    if (this.getActivityState() === 'error') {
      // If the activity is in an error state, then always use the error class
      iconStateClass = this.errorIconStateClass;
    } else {
      // Otherwise, get it from the supported activity type
      const activityTypeId = this.getActivityTypeId();
      if (activityTypeId in this.supportedActivities) {
        iconStateClass = this.supportedActivities[activityTypeId].iconStateClass;
      }
    }

    this.iconStateClass = iconStateClass;
  };

  public save = (activity: Activity): Observable<Activity> => {
    this.onUpdate.emit(activity);
    return of(activity);
  };

  // /** Saves the given activity to the activity service - creates it if it does not have an ID */
  // public save = (activity: IActivityModel): Observable<IActivityModel> => {
  //   // let promise: Promise<IActivityModel>;
  //
  //   //XTODO:  implement activityService save/update calls as observables, then rewrite follows
  //
  //   // if (!activity.activityId) {
  //   //     promise = activity.$save();
  //   // } else {
  //   //     promise = activity.$update();
  //   // }
  //
  //   // Save the repeaterId and re-apply it on the activity when it returns from the service
  //   // const repeaterId = activity.$repeaterId;
  //   // promise.then((activityResult) => {
  //   //     activityResult.$repeaterId = repeaterId;
  //   //     deferred.resolve(activityResult);
  //
  //   //     const note = activityResult as INoteActivity;
  //   //     if (note !== undefined && note.activityType === 'note') {
  //   //         note.isPrivate = this.isNotePrivate;
  //   //     }
  //
  //   //     this.onUpdate();
  //   // }).catch((reason) => {
  //   //     deferred.reject(reason);
  //   // });
  //
  //   // // Finally, return the promise
  //   // return deferred.promise;
  //   // return new Promise(r=> r(activity));
  //   if (activity.activityId) {
  //     return this.http.put<IActivityModel>(
  //       this.appSettings.ticketServiceUrl + `${this.ticket.ticketId}/activities/${activity.activityId}`,
  //       activity
  //     );
  //   } else {
  //     return this.http.post<IActivityModel>(
  //       this.appSettings.ticketServiceUrl + `${this.ticket.ticketId}/activities/`,
  //       activity
  //     );
  //   }
  // };

  // public delete = (activity: IActivityModel): Observable<IActivityModel> => {
  //   return this.http.delete<IActivityModel>(
  //     this.appSettings.ticketServiceUrl + `${this.ticket.ticketId}/activities/${activity.activityId}`
  //   );
  //   // return new Promise(r => r(activity));
  //   // if (activity.activityId) {
  //   //     return activity.$delete();
  //   // }
  //   // const deferred = this.$q.defer<IActivityModel>();
  //   // deferred.resolve(activity);
  //   // return deferred.promise;
  // };

  public getUserName = (userId: string, callback: (userName: string) => void) => {
    if (!!userId) {
      this.getUserInfo(userId, userDetails => {
        callback(userDetails.displayName);
      });
    }
  };

  /** Gets the activity type for the controller - type on activity takes precedent over activity type */
  private getActivityTypeId = (): number => {
    if (this.activity && this.activity.activityTypeId) {
      return this.activity.activityTypeId;
    }

    return this.activityTypeId;
  };

  private updateCreationUser = () => {
    if (this.activity) {
      this.creationUser = this.activity.createdBy;
      // TODO: Confirm if we need to get full detail
      // const creationUserId = this.activity.creationUserId;
      // if (!!creationUserId) {
      //   this.getUserInfo(creationUserId, (userInfo) => {
      //     this.activity.$$creationUserInfo = userInfo;
      //     this.creationUser = userInfo.displayName;
      //   });
      // }
    }
  };

  private updateUpdateUser = () => {
    if (this.activity) {
      this.updateUser = this.activity.updatedBy;
      // TODO: Confirm if we need to get full detail
      // const updateUserId = this.activity.updateUserPublicId;
      // if (!!updateUserId) {
      //   this.getUserInfo(updateUserId, (userInfo) => {
      //     this.activity.$$updateUserInfo = userInfo;
      //     this.updateUser = userInfo.displayName;
      //   });
      // }
    }
  };

  private getUserInfo = (userid: string, callback: (userInfo: UserDetails) => void) => {
    this.userContextService.getUserInfo(userid).subscribe(
      // TODO: Removed Forced Type
      (userInfo: hd.IUserInfoModel) => callback((<unknown>userInfo) as UserDetails),
      (error: hd.IError) => {
        if (error.status === 404) {
          // If user is not found, then log the error and move on
          // this.$log.error(error);
          error.isHandled = true;
        }
      }
    );
  };

  private createIconClass() {
    setTimeout(() => {
      this.iconClass = this.defaultIconClass;
      const activityTypeId = this.getActivityTypeId();
      if (activityTypeId in this.supportedActivities) {
        this.iconClass = this.supportedActivities[activityTypeId].iconClass;
      }
      if (this.activity && this.activity.getCustomIconClass) {
        this.iconClass = this.activity.getCustomIconClass();
      }
    });
  }

  delete() {
    this.onDelete.emit(this.activity);
  }
}
